import type { CustomFormFields, CustomLayoutElements } from ".";
import type {
  BaseFormFieldDefinition,
  FormFieldDefinition,
} from "./form-field";
import type {
  BaseLayoutElementDefinition,
  LayoutElementDefinition,
} from "./layout-element";

type DeriveString<T> = T extends string ? T : never;

export type TabCompletionStatus = "not-started" | "in-progress" | "complete";

export class FormTab<
  FormValues,
  CustomFormFieldDefinitions extends CustomFormFields = never,
  CustomLayoutElementDefinitions extends CustomLayoutElements = never,
> {
  #definition: FormTabDefinition;

  #formFields: (
    | CustomFormFieldDefinitions[keyof CustomFormFieldDefinitions]
    | BaseFormFieldDefinition<
        DeriveString<
          FormFieldDefinition["type"] | keyof CustomFormFieldDefinitions
        >
      >
    | FormFieldDefinition
    | CustomLayoutElementDefinitions[keyof CustomLayoutElementDefinitions]
    | BaseLayoutElementDefinition<
        DeriveString<
          LayoutElementDefinition["type"] | keyof CustomLayoutElementDefinitions
        >
      >
    | LayoutElementDefinition
  )[] = [];

  #childTabs: FormTab<
    FormValues,
    CustomFormFieldDefinitions,
    CustomLayoutElementDefinitions
  >[] = [];

  #validationIndicatorSlot: React.ReactNode;
  #hasErrors = false;
  #completionStatus?: TabCompletionStatus;

  get formFields() {
    return this.#formFields;
  }

  get completionStatus() {
    return this.#completionStatus;
  }

  set completionStatus(value: TabCompletionStatus | undefined) {
    this.#completionStatus = value;
  }

  get label() {
    return this.#definition.label;
  }

  get fullLabel() {
    return this.#definition.fullLabel;
  }

  get id() {
    return this.#definition.id;
  }

  get childTabs() {
    return this.#childTabs;
  }

  get validationIndicatorSlot() {
    return this.#validationIndicatorSlot;
  }

  get hasErrors() {
    return this.#hasErrors;
  }

  set hasErrors(value: boolean) {
    this.#hasErrors = value;
  }

  get isVisible() {
    return this.#definition.isVisible;
  }

  constructor(
    definition: FormTabDefinition,
    private _onChildTabAdded?: () => void
  ) {
    this.#definition = definition;
    this.#validationIndicatorSlot = definition.validationIndicatorSlot;
  }

  addFormField<
    FieldDefinition extends
      | CustomFormFieldDefinitions[keyof CustomFormFieldDefinitions]
      | BaseFormFieldDefinition<
          DeriveString<
            FormFieldDefinition["type"] | keyof CustomFormFieldDefinitions
          >
        >
      | FormFieldDefinition,
  >(field: FieldDefinition) {
    this.#formFields.push(field);
  }

  hasFormField(name: string): boolean {
    return this.#formFields.some((field) => {
      return (field as BaseFormFieldDefinition)?.name === name;
    });
  }

  addLayoutElement<
    LayoutDefinition extends
      | CustomLayoutElementDefinitions[keyof CustomLayoutElementDefinitions]
      | BaseLayoutElementDefinition<
          DeriveString<
            | LayoutElementDefinition["type"]
            | keyof CustomLayoutElementDefinitions
          >
        >
      | LayoutElementDefinition,
  >(element: LayoutDefinition) {
    this.#formFields.push(element);
  }

  addChildTab(
    definition: FormTabDefinition
  ): FormTab<
    FormValues,
    CustomFormFieldDefinitions,
    CustomLayoutElementDefinitions
  > {
    const tab = new FormTab<
      FormValues,
      CustomFormFieldDefinitions,
      CustomLayoutElementDefinitions
    >(definition);
    this.childTabs.push(tab);
    this._onChildTabAdded?.();
    return tab;
  }
}

export interface FormTabDefinition {
  label: string;
  fullLabel?: string;
  id: string;
  validationIndicatorSlot?: React.ReactNode;
  hasErrors?: boolean;
  position?: number;
  /** When set, the tab (and its content) is hidden unless this returns true. */
  isVisible?: (formValues: Record<string, unknown>) => boolean;
}
